import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { v4 as uuidv4 } from 'uuid';

import { useFileEncryptionUpload } from '@/entities/file/hooks/useFileEncryptionUpload';
import { MAX_UPLOAD_FILES_COUNT } from '@/features/inbox';
import { useNotices } from '@/features/notices/useNotices';
import { files as fileHelpers } from '@/shared/helpers';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';

// TODO rework import
import { FileItemInfo } from '../modals/containers/UploadFiles/types';

export type UploadResult = {
  success: boolean;
  filePaths: string[];
};

export const useFiles = () => {
  // TODO rework i18n
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'uploadFiles' });
  const notices = useNotices();
  const { encryptAndUploadFile } = useFileEncryptionUpload({ useDirectUrl: true });

  const [files, setFiles] = useState<FileItemInfo[]>([]);
  const [uploading, setUploading] = useState(false);

  return useMemo(() => {
    const updateFile = (id: string, data: Partial<FileItemInfo>) => {
      const file = files.find((iFile) => iFile.id === id);

      if (file) {
        // TODO rework
        Object.assign(file, data);
        setFiles(files.slice(0));
      }
    };

    const addFiles = (items: File[]) => {
      if (files.length + items.length > MAX_UPLOAD_FILES_COUNT) {
        notices.error(t('errors.maxFiles', { count: MAX_UPLOAD_FILES_COUNT }), {
          key: 'inboxMaxFiles',
        });
        return;
      }

      const newList = items.map((file) => ({
        id: uuidv4(),
        file,
        isValidType: fileHelpers.isValidType('attachments', file.type),
        isValidSize: fileHelpers.isValidSize(file.size),
      }));

      setFiles(files.concat(newList));
    };

    const removeFile = (id: string) => {
      const list = files.filter((file) => file.id !== id);
      setFiles(list);
    };

    const changeComment = (id: string, comment: string) => {
      updateFile(id, { comment });
    };

    const uploadFiles = async (attached?: boolean): Promise<UploadResult> => {
      setUploading(true);

      const newList = files
        .filter((iFile) => !iFile.uploaded && iFile.isValidType && iFile.isValidSize)
        .map((iFile) => ({ ...iFile, isUploadError: false, uploading: true }));

      setFiles(newList);

      const uploadingRequests = newList.map((iFile) => {
        const itemData: Partial<FileMetaInfoDto> = { comment: iFile.comment };
        if (attached) {
          itemData.isFileAttached = true;
        }

        // TODO use only uploading files without adding in collection
        return encryptAndUploadFile(iFile.file, { itemData })
          .then((data) => {
            updateFile(iFile.id, { uploading: false, uploaded: true });
            return data;
          })
          .catch(() => {
            updateFile(iFile.id, { uploading: false, isUploadError: true });
            throw new Error('Uploading error');
          });
      });

      const resUploading = await Promise.allSettled(uploadingRequests);
      const hasError = resUploading.some((res) => res.status === 'rejected' || res.value === null);

      const filePaths = resUploading
        .filter(
          (item): item is PromiseFulfilledResult<string> =>
            item.status === 'fulfilled' && item.value !== null,
        )
        .map((item) => item.value);

      setUploading(false);
      return { success: !hasError, filePaths };
    };

    return { files, uploading, addFiles, removeFile, changeComment, uploadFiles };
  }, [files, uploading, encryptAndUploadFile]);
};
